import React, { memo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Button,
  ButtonGroup,
  Icon,
  Menu,
  MenuItem,
  MenuList,
  MenuButton,
  IconButton,
  Link,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaEllipsisV,
  FaChevronDown,
  FaCopy,
  FaPen,
  FaRocket,
  FaReply,
  FaShareAlt,
  FaExternalLinkAlt,
  FaTrash,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const PostMenu = ({
  buttonProps,
  onDelete,
  onEdit,
  onMultiply,
  onOpen,
  onPublishNow,
  onReuse,
  onShare,
  socialUrl,
  labelVisible,
  ...rest
}) => {
  const { t } = useTranslation(["planner"]);
  const canEditPosts = useSelector(
    (state) => state.permissions.workspacePermissionChecks.canEditPosts
  );

  if (canEditPosts) {
    return (
      <ButtonGroup size="sm" isAttached={labelVisible} variant="solid">
        {labelVisible && onEdit && (
          <Button
            onClick={onEdit}
            variant="solid"
            leftIcon={<Icon size="sm" as={FaPen} />}
            size="sm"
          >
            {t("posts.edit")}
          </Button>
        )}
        {labelVisible && !onEdit && (
          <Button
            onClick={onReuse}
            variant="solid"
            leftIcon={<Icon size="sm" as={FaReply} />}
            size="sm"
          >
            {t("posts.repost")}
          </Button>
        )}
        <Menu
          minWidth="6"
          autoSelect={false}
          {...rest}
          placement="bottom-end"
          variant="solid"
          isLazy
        >
          <MenuButton
            variant={labelVisible ? "solid" : "ghost"}
            as={IconButton}
            icon={labelVisible ? <FaChevronDown /> : <FaEllipsisV />}
            aria-label="Options"
            onClick={(e) => e.stopPropagation()}
            borderLeft={labelVisible ? "1px solid" : "none"}
            borderLeftColor="whiteAlpha.400"
            {...buttonProps}
          />
          <MenuList
            w="130px"
            minW="130px"
            fontSize="xs"
            onClick={(e) => e.stopPropagation()}
          >
            {onEdit && (
              <MenuItem
                onClick={onEdit}
                icon={<Icon fontSize="sm" as={FaPen} />}
              >
                {t("posts.edit")}
              </MenuItem>
            )}
            {onPublishNow && (
              <MenuItem
                onClick={onPublishNow}
                icon={<Icon fontSize="sm" as={FaRocket} />}
              >
                {t("posts.publish_now")}
              </MenuItem>
            )}
            {onReuse && (
              <MenuItem
                onClick={onReuse}
                icon={<Icon fontSize="sm" as={FaReply} />}
              >
                {t("posts.repost")}
              </MenuItem>
            )}
            {onMultiply && (
              <MenuItem
                onClick={onMultiply}
                icon={<Icon fontSize="sm" as={FaCopy} />}
              >
                {t("posts.multiply")}
              </MenuItem>
            )}
            {onShare && socialUrl && (
              <MenuItem
                onClick={onShare}
                icon={<Icon fontSize="sm" as={FaShareAlt} />}
              >
                {t("posts.shareLink")}
              </MenuItem>
            )}
            {socialUrl && (
              <MenuItem
                as={Link}
                onClick={onOpen}
                href={socialUrl}
                icon={<Icon fontSize="sm" as={FaExternalLinkAlt} />}
                isExternal
                sx={{
                  "&:focus, &:hover": {
                    textDecoration: "none",
                  },
                }}
              >
                {t("posts.open")}
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                onClick={onDelete}
                icon={<Icon fontSize="sm" as={FaTrash} />}
              >
                {t("posts.delete")}
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </ButtonGroup>
    );
  }

  return (
    <Tooltip
      label={t("no_post_edit_permission", { ns: "common" })}
      placement="bottom"
      hasArrow
      textAlign="center"
      shouldWrapChildren
    >
      <Menu isLazy minWidth="6" autoSelect={false} {...rest}>
        <MenuButton
          variant="ghost"
          as={IconButton}
          icon={<FaEllipsisV />}
          aria-label="Options"
          {...buttonProps}
        />
      </Menu>
    </Tooltip>
  );
};

PostMenu.propTypes = {
  buttonProps: PropTypes.object,
  labelVisible: PropTypes.bool,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onMultiply: PropTypes.func,
  onOpen: PropTypes.func,
  onPublishNow: PropTypes.func,
  onReuse: PropTypes.func,
  onShare: PropTypes.func,
  socialUrl: PropTypes.string,
};

PostMenu.defaultProps = {
  buttonProps: undefined,
  labelVisible: false,
  onDelete: undefined,
  onEdit: undefined,
  onMultiply: undefined,
  onOpen: undefined,
  onPublishNow: undefined,
  onReuse: undefined,
  onShare: undefined,
  socialUrl: undefined,
};

export default memo(PostMenu);
